import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, Chip, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow, Divider,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxResultListDto, type DxResultLineDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const STATUSES = ["", "Entered", "Verified", "Reviewed", "Approved", "Amended"];
const flagColor = (f: string) => f.startsWith("Critical") ? "error" : f === "Low" || f === "High" || f === "Abnormal" ? "warning" : "default";

export function DiagnosticResultsPage() {
  const [status, setStatus] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const query = useQuery({ queryKey: ["dx-results", status], queryFn: () => diagnosticsApi.listResults(undefined, status || undefined) });

  const columns: GridColDef<DxResultListDto>[] = [
    { field: "id", headerName: "Result", width: 130, valueGetter: v => (v as string).slice(0, 8) },
    { field: "status", headerName: "Status", width: 120, renderCell: p => <Chip size="small" label={p.row.status} variant="outlined" /> },
    { field: "versionLabel", headerName: "Ver.", width: 70 },
    { field: "verifiedBy", headerName: "Verified", width: 120 },
    { field: "reviewedBy", headerName: "Reviewed", width: 120 },
    { field: "approvedBy", headerName: "Approved", width: 120 },
    { field: "hasDeltaBreach", headerName: "Δ", width: 60, sortable: false, renderCell: p => p.row.hasDeltaBreach ? <Chip size="small" color="warning" label="Δ!" /> : null },
    { field: "createdAt", headerName: "Created", width: 160, valueGetter: v => v ? formatDateTime(v as string) : "" },
    { field: "actions", headerName: "", flex: 1, minWidth: 100, sortable: false, align: "right", renderCell: p => <Button size="small" onClick={() => setDetailId(p.row.id)}>Open</Button> },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Lab results" />
      <Paper sx={{ p: 2 }}>
        <TextField select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ width: 200, mb: 2 }}>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
        </TextField>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 560 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
      <ResultDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Box>
  );
}

function ResultDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const canVerify = useAuth(s => s.hasPermission("diagnostics.result.verify"));
  const canReview = useAuth(s => s.hasPermission("diagnostics.result.review"));
  const canApprove = useAuth(s => s.hasPermission("diagnostics.result.approve"));
  const [signature, setSignature] = useState("");
  const query = useQuery({ queryKey: ["dx-result", id], queryFn: () => diagnosticsApi.getResult(id!), enabled: !!id });
  const r = query.data;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["dx-result", id] }); qc.invalidateQueries({ queryKey: ["dx-results"] }); };
  const verify = useMutation({ meta: { successMessage: "Result verified (tier 1)" }, mutationFn: () => diagnosticsApi.verifyResult(id!, { signature }), onSuccess: refresh });
  const review = useMutation({ meta: { successMessage: "Result reviewed (tier 2)" }, mutationFn: () => diagnosticsApi.reviewResult(id!, { signature }), onSuccess: refresh });
  const approve = useMutation({ meta: { successMessage: "Result approved / released (tier 3)" }, mutationFn: () => diagnosticsApi.approveResult(id!, { signature }), onSuccess: refresh });

  const lineCols = (l: DxResultLineDto) => l.numericValue ?? l.textValue ?? "";
  const needsSignoff = r?.status === "Entered" || r?.status === "Verified" || r?.status === "Reviewed";

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Result {r ? r.status : ""}</DialogTitle>
      <DialogContent dividers>
        {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
        {r && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={r.status} />
              <Chip size="small" variant="outlined" label={`v${r.versionLabel}`} />
              {r.status === "Amended" && <Chip size="small" color="error" label="AMENDED — supersedes previous" />}
              {r.hasCriticalFlag && <Chip size="small" color="error" label="Critical" />}
              {r.hasAbnormalFlag && !r.hasCriticalFlag && <Chip size="small" color="warning" label="Abnormal" />}
              {r.hasDeltaBreach && <Chip size="small" color="warning" label="Delta-check breach" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {r.verifiedBy && `Verified by ${r.verifiedBy}`}{r.reviewedBy && ` · Reviewed by ${r.reviewedBy}`}{r.approvedBy && ` · Approved by ${r.approvedBy}`}
            </Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Analyte</TableCell><TableCell>Value</TableCell><TableCell>Unit</TableCell><TableCell>Flag</TableCell><TableCell>Range</TableCell><TableCell>Δ</TableCell></TableRow></TableHead>
              <TableBody>
                {r.lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.analyte}</TableCell><TableCell>{lineCols(l)}</TableCell><TableCell>{l.unit}</TableCell>
                    <TableCell><Chip size="small" label={l.flag} color={flagColor(l.flag)} variant={l.flag === "Normal" || l.flag === "None" ? "outlined" : "filled"} /></TableCell>
                    <TableCell>{l.rangeUsed}</TableCell>
                    <TableCell>{l.delta ?? ""}{l.deltaBreached && <Chip size="small" color="warning" label="Δ!" sx={{ ml: 0.5 }} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {r.amendments.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} /><Typography variant="subtitle2">Amendments</Typography>
                <Table size="small">
                  <TableHead><TableRow><TableCell>#</TableCell><TableCell>Analyte</TableCell><TableCell>Prev → New</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                  <TableBody>{r.amendments.map(a => <TableRow key={a.version}><TableCell>{a.version}</TableCell><TableCell>{a.analyte}</TableCell><TableCell>{a.previousNumericValue ?? "—"} → {a.newNumericValue ?? "—"}</TableCell><TableCell>{a.reason}</TableCell></TableRow>)}</TableBody>
                </Table>
              </>
            )}
            {(verify.isError || review.isError || approve.isError) && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(verify.error ?? review.error ?? approve.error)}</Alert>}
            {needsSignoff && (
              <TextField label="Digital signature" size="small" fullWidth sx={{ mt: 2 }} value={signature}
                onChange={e => setSignature(e.target.value)} helperText="Required to sign off this tier (plan §4.10)" />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {r?.status === "Entered" && canVerify && <Button onClick={() => verify.mutate()} disabled={verify.isPending || !signature}>1 · Verify</Button>}
        {r?.status === "Verified" && canReview && <Button onClick={() => review.mutate()} disabled={review.isPending || !signature}>2 · Review</Button>}
        {r?.status === "Reviewed" && canApprove && <Button variant="contained" onClick={() => approve.mutate()} disabled={approve.isPending || !signature}>3 · Approve</Button>}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
